import {
  flex,
  mv,
  pb,
  pt,
  width,
  SuccessMessage,
} from '@openeducation/oe-rn-components';
import { selectedLiveClassFeedbackAtom } from '@openeducation/oe-rn-jotai-atoms';
import { getUrlParams } from '@openeducation/oe-rn-services';
import type { LiveClassPendingFeedback } from '@openeducation/oe-rn-shared-types';
import { styled } from '@openeducation/oe-rn-themes';
import { useAtom } from 'jotai';
import { rgba } from 'polished';
import * as React from 'react';
import { View } from 'react-native';

import { NoPCPendingFeedbacks } from '../../organisms/no-pc-pending-feedbacks/no-pc-pending-feedbacks';
import { PCCommonFeedback } from '../../organisms/pc-common-feedback/pc-common-feedback';
import { PCFeedbackForm } from '../../organisms/pc-feedback-form/pc-feedback-form';
import { PCPendingFeedbackList } from '../../organisms/pc-pending-feedback-list/pc-pending-feedback-list';
import { SelectedPCFeedbackPanel } from '../../organisms/selected-pc-feedback-panel/selected-pc-feedback-panel';
import { PortalLayout } from '../../templates/portal-layout/portal-layout';

export interface PendingFeedbacksProps {
  pendingFeedbacks?: LiveClassPendingFeedback[];
  onSelectedFeedbackChange: (studentId?: number, classroomId?: number) => void;
  onFeedbackSubmitted: () => void;
  goToNextFeedback: () => void;
}

export const PendingFeedbacks = ({
  pendingFeedbacks,
  onSelectedFeedbackChange,
  onFeedbackSubmitted,
  goToNextFeedback,
}: PendingFeedbacksProps) => {
  const [selectedLiveClassFeedback, setSelectedLiveClassFeedback] = useAtom(
    selectedLiveClassFeedbackAtom
  );
  const params = getUrlParams();

  React.useEffect(() => {
    if (params?.noFeedback) {
      setSelectedLiveClassFeedback(undefined);
      return;
    }
    const selectedFeedback = pendingFeedbacks?.find(
      (f) =>
        f.studentId === params?.studentId &&
        f.classroomId === params?.classroomId
    );
    if (selectedFeedback) {
      setSelectedLiveClassFeedback(selectedFeedback);
    } else if (pendingFeedbacks?.[0]) {
      setSelectedLiveClassFeedback(pendingFeedbacks[0]);
    }
  }, [
    params?.classroomId,
    params?.noFeedback,
    params?.studentId,
    pendingFeedbacks,
    setSelectedLiveClassFeedback,
  ]);

  const handleSelectedFeedbackChange = (feedback: LiveClassPendingFeedback) => {
    setSelectedLiveClassFeedback(feedback);
    onSelectedFeedbackChange(feedback.studentId, feedback.classroomId);
  };

  if (pendingFeedbacks && pendingFeedbacks.length)
    return (
      <PCCommonFeedback
        panelStyle={[width(780)]}
        rightSideBar={
          <PCPendingFeedbackList
            onSelectedFeedbackChange={handleSelectedFeedbackChange}
            pendingFeedbacks={pendingFeedbacks}
            isFeedbackSelected={!!selectedLiveClassFeedback}
          />
        }
      >
        {!selectedLiveClassFeedback ? (
          <SuccessMessage
            title="You have pending feedback."
            body="Make sure to complete all of your feedback by the end of your shift."
            continueLabel="Next feedback"
            onContinue={goToNextFeedback}
            style={[mv(0)]}
          />
        ) : (
          <>
            <SelectedPCFeedbackPanel />
            <Divider />
            <PCFeedbackForm onFeedbackSubmitted={onFeedbackSubmitted} />
          </>
        )}
      </PCCommonFeedback>
    );
  else
    return (
      <PortalLayout>
        <View
          style={[
            flex('justify-content-center', 'align-items-center'),
            pt(58),
            pb(80),
          ]}
        >
          <NoPCPendingFeedbacks />
        </View>
      </PortalLayout>
    );
};

const Divider = styled.View`
  height: 1px;
  width: 700px;
  margin-top: 40px;
  margin-bottom: 40px;
  background-color: ${({ theme }) => rgba(theme.color.primary, 0.05)};
`;
